import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function AboutPage() {
  const contactFormUrl = 'https://readdy.ai/api/form/d9u0ujd3pcjqs2fcsk8g';
  const subscribeFormUrl = 'https://readdy.ai/api/form/d9u0ujd3pcjqs2fcsk90';
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState(false);

  useEffect(() => {
    document.title = '关于我们 - 一品楼论坛 | 品牌介绍与联系方式';
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '一品楼论坛是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '一品楼论坛是一个汇聚优质内容的综合性中文交流社区，提供最新资讯、热门话题讨论与会员互动。涵盖技术交流、生活分享、投资理财、职场成长、数码科技、读书学习、娱乐休闲、健康养生八大主题分类。',
        },
      },
      {
        '@type': 'Question',
        name: '如何注册一品楼论坛账号？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '注册非常简单，点击网站右上角的"注册加入"按钮，填写邮箱地址、用户名和密码即可完成注册。注册后建议完善个人资料和设置头像，方便其他会员认识你。',
        },
      },
      {
        '@type': 'Question',
        name: '一品楼论坛是否免费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '一品楼论坛完全免费！所有会员都可以免费浏览帖子、发布内容、参与讨论。我们还为活跃会员提供积分奖励机制，积分可以兑换社区特权。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在一品楼论坛发布帖子？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '注册并登录后，进入任意分类板块，点击"发布新帖"按钮即可开始写作。我们支持Markdown格式编辑，也可以直接输入纯文本。发帖前请先阅读社区规范，确保内容符合版规。',
        },
      },
      {
        '@type': 'Question',
        name: '一品楼论坛的内容质量如何保证？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们有多重机制保证内容质量：(1) 人工审核团队实时监控内容质量；(2) 社区会员举报机制；(3) 优质内容推荐算法优先展示高质量帖子；(4) 版主定期整理精华内容并置顶推荐。',
        },
      },
    ],
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于一品楼论坛',
    description: '一品楼论坛品牌介绍，了解我们的发展历程、社区理念、口碑评价与联系方式。',
    url: 'https://wouzfuo.cn/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '一品楼论坛',
      url: 'https://wouzfuo.cn',
    },
  };

  const milestones = [
    { year: '2020', title: '论坛成立', desc: '一品楼论坛正式上线，初期聚焦技术交流和数码科技两大板块' },
    { year: '2021', title: '快速成长', desc: '注册会员突破10,000人，新增生活分享和投资理财板块' },
    { year: '2022', title: '版图扩展', desc: '覆盖八大主题分类，月活跃用户超过20,000人' },
    { year: '2023', title: '内容升级', desc: '推出社区优质内容推荐算法，帖子质量大幅提升' },
    { year: '2024', title: '生态完善', desc: '上线积分系统和会员等级制度，社区生态更加完善' },
    { year: '2025', title: '行业标杆', desc: '注册会员突破50,000人，成为中国最具影响力的综合交流社区之一' },
  ];

  const testimonials = [
    {
      name: '技术达人小王',
      role: '资深会员 / 技术交流版主',
      content: '一品楼论坛是我每天必逛的社区。这里不仅有高质量的技术讨论，更重要的是社区氛围非常好，大家互相帮助、共同进步。我在论坛认识了很多志同道合的朋友。',
    },
    {
      name: '理财小陈',
      role: '投资理财板块活跃用户',
      content: '在一品楼论坛的投资理财板块学到了很多实用的理财知识。从基金定投到资产配置，社区里的前辈们分享的经验都非常有参考价值。可以说这个论坛改变了我的理财观念。',
    },
    {
      name: '追剧小能手',
      role: '娱乐休闲板块版主',
      content: '最让我感动的是这里的社区温度。无论是推荐一部好剧，还是分享生活中的小确幸，总能收到很多温暖的回响。一品楼论坛就像是互联网世界里的一个温馨客厅。',
    },
  ];

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess(false);
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = formData.get('phone_alt') as string;
    if (honeypotValue && honeypotValue.trim() !== '') {
      setFormSuccess(true);
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });
      const response = await fetch(contactFormUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormSuccess(true);
        form.reset();
      } else {
        setFormError(parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试。');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试。');
    }
  };

  const handleSubscribeSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess(false);
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = formData.get('mobile_alt') as string;
    if (honeypotValue && honeypotValue.trim() !== '') {
      setFormSuccess(true);
      form.reset();
      return;
    }
    formData.delete('mobile_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });
      const response = await fetch(subscribeFormUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormSuccess(true);
        form.reset();
      } else {
        setFormError(parsed?.meta?.message || parsed?.message || '订阅失败，请稍后再试。');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试。');
    }
  };

  return (
    <>
      <SchemaOrg data={aboutSchema} />
      <SchemaOrg data={faqSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100/70 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
              <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mt-3 font-heading">
                关于一品楼论坛
              </h1>
              <p className="text-foreground-600 mt-1">了解我们的故事与愿景</p>
            </div>
          </div>

          {/* Brand Introduction */}
          <section className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                品牌介绍
              </h2>
              <p className="text-foreground-600 mt-2 max-w-2xl mx-auto">
                一品楼论坛是一个以内容为核心的综合性中文交流社区
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-lightbulb-line text-lg"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">我们的使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  打造一个开放、友善、高质量的中文交流社区，让每一位用户都能在这里找到有价值的内容和志同道合的朋友。
                </p>
              </div>
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-eye-line text-lg"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  成为中国最具影响力的综合性内容社区，构建一个连接知识、连接兴趣、连接人的信息共享平台。
                </p>
              </div>
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-heart-line text-lg"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">我们的价值观</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  坚持内容品质优先、坚持用户至上、坚持社区自治。我们相信优秀的内容和友善的氛围是一个社区的核心生命力。
                </p>
              </div>
            </div>

            <div className="p-6 rounded-xl bg-background-50 border border-background-200/70 mb-12">
              <h3 className="text-lg font-semibold text-foreground-900 mb-3 font-heading">关于一品楼论坛</h3>
              <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                一品楼论坛创立于2020年，经过六年多的发展，已经成为中文互联网最具影响力的综合性交流社区之一。目前拥有超过50,000名注册会员，累计发布优质帖子超过12万篇，覆盖技术交流、生活分享、投资理财、职场成长、数码科技、读书学习、娱乐休闲、健康养生八大核心板块。
              </p>
              <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                我们的社区以高质量的内容和友善的交流氛围著称。在这里，技术大牛分享前沿知识，理财达人交流投资心得，生活爱好者记录美好瞬间，职场前辈传授经验心得。无论你对什么领域感兴趣，都能在一品楼论坛找到属于自己的圈子。
              </p>
              <p className="text-sm text-foreground-700 leading-relaxed">
                一品楼论坛坚持用户共创的理念，社区的内容主要由活跃的会员贡献，管理团队负责维护秩序和优质内容的推荐。我们相信，最好的社区是由用户自己建立和维系的。欢迎加入一品楼论坛，成为我们大家庭的一员！
              </p>
            </div>
          </section>

          {/* Milestones */}
          <section className="bg-background-100/70 py-12 md:py-16">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  发展历程
                </h2>
                <p className="text-foreground-600 mt-2">六年砥砺前行，一步一个脚印</p>
              </div>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-accent-300/60"></div>
                <div className="flex flex-col gap-6">
                  {milestones.map((m, i) => (
                    <div key={m.year} className={`flex items-start gap-6 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                        <div className="p-4 rounded-xl bg-background-50 border border-background-200/70">
                          <h4 className="text-sm font-semibold text-foreground-900 font-heading">{m.title}</h4>
                          <p className="text-xs text-foreground-600 mt-1">{m.desc}</p>
                        </div>
                      </div>
                      <div className="relative z-10 flex-shrink-0">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-xs font-bold">
                          {m.year.slice(2)}
                        </div>
                      </div>
                      <div className={`flex-1 ${i % 2 === 0 ? 'md:text-left' : 'md:text-right'}`}>
                        <div className="p-4 rounded-xl bg-background-50 border border-background-200/70 md:hidden">
                          <h4 className="text-sm font-semibold text-foreground-900 font-heading">{m.title}</h4>
                          <p className="text-xs text-foreground-600 mt-1">{m.desc}</p>
                        </div>
                        <div className="hidden md:block md:opacity-0">{/* spacer */}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                用户口碑
              </h2>
              <p className="text-foreground-600 mt-2">听听社区成员怎么说</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-1 text-accent-500 mb-3">
                    {[...Array(5)].map((_, j) => (
                      <i key={j} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                  <div className="flex items-center gap-3 border-t border-background-200/70 pt-3">
                    <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 font-semibold text-sm">
                      {t.name[0]}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="bg-background-100/70 py-12 md:py-16">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  常见问题
                </h2>
                <p className="text-foreground-600 mt-2">快速了解一品楼论坛</p>
              </div>
              <div className="flex flex-col gap-4">
                {(faqSchema.mainEntity as Array<{ name: string; acceptedAnswer: { text: string } }>).map((faq, i) => (
                  <details key={i} className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between p-5 cursor-pointer select-none">
                      <h4 className="text-sm font-semibold text-foreground-900 pr-4 font-heading">{faq.name}</h4>
                      <i className="ri-arrow-down-s-line text-foreground-400 transition-transform group-open:rotate-180"></i>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-700 leading-relaxed">{faq.acceptedAnswer.text}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                联系我们
              </h2>
              <p className="text-foreground-600 mt-2">有任何问题或建议，欢迎与我们联系</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
              <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
                <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">电子邮箱</h4>
                <p className="text-xs text-foreground-600">contact@yipinlou.com</p>
              </div>
              <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
                <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-customer-service-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">客服QQ群</h4>
                <p className="text-xs text-foreground-600">88888888</p>
              </div>
              <div className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
                <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">办公地址</h4>
                <p className="text-xs text-foreground-600">北京市朝阳区科技园路88号</p>
              </div>
            </div>

            <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
              <h3 className="text-lg font-semibold text-foreground-900 mb-4 font-heading">发送消息</h3>

              {formSuccess && (
                <div className="mb-4 p-3 rounded-lg bg-accent-100 text-accent-800 text-sm">
                  消息已发送！感谢您的来信，我们会在1-2个工作日内回复。
                </div>
              )}
              {formError && (
                <div className="mb-4 p-3 rounded-lg bg-primary-100 text-primary-800 text-sm">
                  {formError}
                </div>
              )}

              <form id="contact-form" data-readdy-form onSubmit={handleContactSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      您的姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入留言内容（不超过500字）"
                  ></textarea>
                </div>
                <div className="spam-field-wrapper">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <button
                  type="submit"
                  className="px-6 py-2.5 rounded-lg text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  发送消息
                </button>
              </form>
            </div>
          </section>

          {/* Newsletter */}
          <section className="bg-background-100/70 py-12 md:py-16">
            <div className="max-w-xl mx-auto px-4 md:px-6 text-center">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-2 font-heading">
                订阅社区资讯
              </h2>
              <p className="text-sm text-foreground-600 mb-6">
                第一时间获取一品楼论坛最新动态、精选内容和活动通知
              </p>

              {formSuccess && (
                <div className="mb-4 p-3 rounded-lg bg-accent-100 text-accent-800 text-sm">
                  订阅成功！感谢您的关注，您将通过邮箱收到社区资讯。
                </div>
              )}

              <form id="subscribe-form" data-readdy-form onSubmit={handleSubscribeSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="输入邮箱地址"
                  className="flex-1 px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                />
                <div className="spam-field-wrapper">
                  <input type="text" name="mobile_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-lg text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  立即订阅
                </button>
              </form>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}